import type { ReactNode } from 'react'
import { Palette, Image, FileStack, PanelTop, PanelBottom, Upload, HardDrive, Sparkles, RotateCcw, Globe } from 'lucide-react'
import type { ModuleSubPage } from '../../config/modules'

/** The AI assistant needs both modules' view permissions (chat plan D6). */
export const AI_CHAT_VIEW_PERMISSION = 'module.ai_chat.view'

export function canUseWebsiteAssistant(permissions: readonly string[]): boolean {
  return permissions.includes(AI_CHAT_VIEW_PERMISSION)
}

export interface WebsiteBuilderSection {
  key: 'assistant' | 'brand' | 'images' | 'pages' | 'navbar' | 'footer' | 'publish' | 'domains' | 'storage' | 'reset'
  labelKey: string
  labelFallback: string
  path: string
  descriptionKey: string
  descriptionFallback: string
  icon: ReactNode
}

export const WEBSITE_BUILDER_SECTIONS: WebsiteBuilderSection[] = [
  {
    key: 'assistant',
    labelKey: 'website.assistant.title',
    labelFallback: 'Assistant IA',
    path: '/app/website/assistant',
    descriptionKey: 'website.landing.assistant.desc',
    descriptionFallback: 'Créer et adapter vos pages par la conversation',
    icon: <Sparkles className="w-8 h-8" />,
  },
  {
    key: 'brand',
    labelKey: 'website.nav.brand',
    labelFallback: 'Brand',
    path: '/app/website/brand',
    descriptionKey: 'website.landing.brand.desc',
    descriptionFallback: 'Logo, couleurs et typographie',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
        />
      </svg>
    ),
  },
  {
    key: 'images',
    labelKey: 'website.nav.images',
    labelFallback: 'Images',
    path: '/app/website/images',
    descriptionKey: 'website.landing.images.desc',
    descriptionFallback: 'Importer et organiser les images',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    key: 'pages',
    labelKey: 'website.nav.pages',
    labelFallback: 'Pages',
    path: '/app/website/pages',
    descriptionKey: 'website.landing.pages.desc',
    descriptionFallback: 'Structure et arborescence du site',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
        />
      </svg>
    ),
  },
  {
    key: 'navbar',
    labelKey: 'website.nav.navbar',
    labelFallback: 'Navbar',
    path: '/app/website/navbar',
    descriptionKey: 'website.landing.navbar.desc',
    descriptionFallback: 'Barre de navigation et en-tête',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h7" />
      </svg>
    ),
  },
  {
    key: 'footer',
    labelKey: 'website.nav.footer',
    labelFallback: 'Footer',
    path: '/app/website/footer',
    descriptionKey: 'website.landing.footer.desc',
    descriptionFallback: 'Pied de page partagé sur tout le site',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
  },
  {
    key: 'publish',
    labelKey: 'website.nav.publish',
    labelFallback: 'Publish',
    path: '/app/website/publish',
    descriptionKey: 'website.landing.publish.desc',
    descriptionFallback: 'Aperçu et publication en pré-production',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.75}
          d="M15 11l-3-3m0 0l-3 3m3-3v8M5 17l-2 2m0 0l2 2m-2-2h14m2-2l-2 2m0 0l2 2"
        />
      </svg>
    ),
  },
  {
    key: 'domains',
    labelKey: 'website.nav.domains',
    labelFallback: 'Domaine personnalisé',
    path: '/app/website/domains',
    descriptionKey: 'website.landing.domains.desc',
    descriptionFallback: 'Servir votre site sur votre propre nom de domaine',
    icon: <Globe className="w-8 h-8" />,
  },
  {
    key: 'storage',
    labelKey: 'website.nav.storage',
    labelFallback: 'Storage',
    path: '/app/website/storage',
    descriptionKey: 'website.landing.storage.desc',
    descriptionFallback: 'Espace utilisé et quota de stockage',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 12H3l9-9 9 9h-2M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
      </svg>
    ),
  },
  {
    key: 'reset',
    labelKey: 'website.nav.reset',
    labelFallback: 'Réinitialiser',
    path: '/app/website/reset',
    descriptionKey: 'website.landing.reset.desc',
    descriptionFallback: 'Repartir de zéro sans toucher au site publié',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
]

export const WEBSITE_BUILDER_MODULE_SUBPAGES: ModuleSubPage[] = WEBSITE_BUILDER_SECTIONS.map(
  ({ labelKey, labelFallback, path }) => {
    const iconMap: Record<string, ReactNode> = {
      assistant: <Sparkles size={16} />,
      brand: <Palette size={16} />,
      images: <Image size={16} />,
      pages: <FileStack size={16} />,
      navbar: <PanelTop size={16} />,
      footer: <PanelBottom size={16} />,
      publish: <Upload size={16} />,
      domains: <Globe size={16} />,
      storage: <HardDrive size={16} />,
      reset: <RotateCcw size={16} />,
    }
    const key = path.split('/').pop() ?? ''
    return { labelKey, labelFallback, path, icon: iconMap[key] }
  }
)
